import { z } from 'zod'
import { isValidPhone } from '@/lib/phone'

/**
 * Form values map 1:1 onto the frozen POST /rent/start contract
 * (`StartRentalBody` in `@rentflowdev/api`):
 * - `tenantMode` discriminates the `contact` union — `{ id }` (existing)
 *   vs `{ name, phone?, email? }` (created inline by the orchestrator);
 * - `rentAmount` / `depositAmount` / `period` → `body.terms`;
 * - `enableAppAccess` → `body.enableAppAccess`.
 * `terms.currency` is fixed to RUB at submit and not user-editable.
 * `dealId` comes from the dialog preset (kanban), never from user input.
 *
 * Validation messages are i18n keys resolved at render time.
 */

// Allows spaces as thousand separators - stripped at submit.
const AMOUNT_PATTERN = /^\d[\d\s]*(?:[.,]\d+)?$/

export const startRentalSchema = z
	.object({
		tenantMode: z.enum(['existing', 'new']),
		// Existing tenant — required when tenantMode === 'existing'
		contactId: z.string().optional(),
		// New tenant — name required when tenantMode === 'new'
		name:  z.string().trim().max(200).optional(),
		// Unconditionally optional here; the phone rule is applied in the
		// superRefine below, gated on tenantMode. The field is only rendered for
		// a new tenant, so validating it always meant a half-typed number left
		// behind when switching to an existing tenant blocked submit with an
		// error on a field nobody can see.
		phone: z.string().trim().optional(),
		email: z
			.string()
			.trim()
			.email('rent.startDialog.validation.emailInvalid')
			.optional()
			.or(z.literal('')),
		propertyId: z.string().min(1, 'rent.startDialog.validation.propertyRequired'),
		rentAmount: z
			.string()
			.trim()
			.min(1, 'rent.startDialog.validation.rentAmountRequired')
			.regex(AMOUNT_PATTERN, 'rent.startDialog.validation.rentAmountInvalid'),
		depositAmount: z
			.string()
			.trim()
			.regex(AMOUNT_PATTERN, 'rent.startDialog.validation.depositAmountInvalid')
			.optional()
			.or(z.literal('')),
		period: z.enum(['MONTHLY', 'WEEKLY', 'QUARTERLY', 'YEARLY']),
		startDate: z.string().min(1, 'rent.startDialog.validation.startDateRequired'),
		endDate: z.string().optional(),
		enableAppAccess: z.boolean(),
	})
	.superRefine((values, ctx) => {
		if (values.tenantMode === 'existing' && !values.contactId) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['contactId'],
				message: 'rent.startDialog.validation.contactRequired',
			})
		}
		if (values.tenantMode === 'new' && !values.name?.trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['name'],
				message: 'rent.startDialog.validation.nameRequired',
			})
		}
		// Both pickers default to today, so equal dates are what a hurried user
		// actually produces — and a tenancy spanning no time bills nothing.
		if (values.endDate && values.startDate && values.endDate <= values.startDate) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endDate'],
				message: 'rent.startDialog.validation.endBeforeStart',
			})
		}
		if (values.tenantMode === 'new' && values.phone && !isValidPhone(values.phone)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['phone'],
				message: 'contacts.editDialog.validation.phoneInvalid',
			})
		}
	})

export type StartRentalFormValues = z.infer<typeof startRentalSchema>

export interface StartRentalPreset {
	contactId?: string
	propertyId?: string
	dealId?: string
	rentAmount?: string
}

export function makeDefaultValues(preset?: StartRentalPreset): StartRentalFormValues {
	return {
		tenantMode:      'existing',
		contactId:       preset?.contactId ?? '',
		name:            '',
		phone:           '',
		email:           '',
		propertyId:      preset?.propertyId ?? '',
		rentAmount:      preset?.rentAmount ?? '',
		depositAmount:   '',
		period:          'MONTHLY',
		startDate:       '',
		endDate:         '',
		enableAppAccess: true,
	}
}
